import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, ScrollText, Settings2, LogOut, Copy, CheckCircle2, FlaskConical, UserCog, GitBranch, Wifi, FileText, ClipboardSignature, ChevronsLeft, ChevronsRight, ChevronDown, ChevronRight, Bell, MessageSquare, Activity as ActivityIcon, Lightbulb, UserSquare2, Database, Mail, ShieldCheck, AlertOctagon } from "lucide-react";
import HeaderSearch from "@/components/HeaderSearch";
import { useAuth } from "@/context/AuthContext";
import { VersionFooter } from "@/components/VersionFooter";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

function PartnerUpdatesBell() {
  return <PartnerUpdatesBellImpl />;
}

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/admin/partners", icon: Users, label: "Partners", testid: "nav-partners" },
  { to: "/admin/customers", icon: UserSquare2, label: "Customers", testid: "nav-customers" },
  { to: "/admin/onboarding", icon: GitBranch, label: "Onboarding", testid: "nav-onboarding" },
  { to: "/admin/tickets", icon: ClipboardSignature, label: "Tickets", testid: "nav-tickets" },
  { to: "/admin/ledger", icon: ScrollText, label: "Financial", testid: "nav-ledger" },
  { to: "/admin/canned-responses", icon: MessageSquare, label: "Canned responses", testid: "nav-canned" },
  { to: "/admin/inbound-inbox", icon: Mail, label: "Inbound inbox", testid: "nav-inbound-inbox" },
  { to: "/admin/internet-plans", icon: Wifi, label: "Internet plans", testid: "nav-internet-plans" },
  { to: "/admin/signups", icon: ClipboardSignature, label: "Signed signups", testid: "nav-signups" },
  { to: "/admin/feedback", icon: Lightbulb, label: "Feedback", testid: "nav-feedback" },
  { to: "/admin/sync-health", icon: ActivityIcon, label: "Sync health", testid: "nav-sync-health" },
  {
    to: "/admin/settings", icon: Settings2, label: "Settings", testid: "nav-settings",
    // Sub-items rendered indented under Settings — grouped here because
    // they're admin-configuration surfaces the day-to-day nav doesn't
    // need to see. The parent still routes to /admin/settings itself.
    children: [
      { to: "/admin/settings", icon: Settings2, label: "Business", testid: "nav-settings-business", tabKey: "business", end: true },
      { to: "/admin/settings?tab=email", icon: Mail, label: "Email", testid: "nav-settings-email", tabKey: "email" },
      { to: "/admin/settings?tab=webhooks", icon: ActivityIcon, label: "Webhooks", testid: "nav-settings-webhooks", tabKey: "webhooks" },
      { to: "/admin/settings?tab=security", icon: ShieldCheck, label: "Security", testid: "nav-settings-security", tabKey: "security" },
      { to: "/admin/settings?tab=danger", icon: AlertOctagon, label: "Danger zone", testid: "nav-settings-danger", tabKey: "danger" },
      { to: "/admin/users", icon: UserCog, label: "Users", testid: "nav-users" },
      { to: "/admin/legal-docs", icon: FileText, label: "Legal docs", testid: "nav-legal-docs" },
      { to: "/admin/backups", icon: Database, label: "Backups", testid: "nav-backups" },
    ],
  },
];

const SIDEBAR_STATE_KEY = "sc.admin.sidebar.collapsed";
const SETTINGS_OPEN_KEY = "sc.admin.settings.open";

export default function AdminShell() {
  const { state, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_STATE_KEY) === "1"; } catch { return false; }
  });
  // Settings section toggle — persists across reloads, and auto-opens
  // whenever the current URL is on Settings or any of its children so
  // the active item is never hidden inside a collapsed section.
  const [settingsOpen, setSettingsOpen] = useState(() => {
    try { return localStorage.getItem(SETTINGS_OPEN_KEY) === "1"; } catch { return false; }
  });
  const settingsChildPaths = ["/admin/settings", "/admin/users", "/admin/legal-docs", "/admin/backups"];
  const isOnSettingsBranch = settingsChildPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  useEffect(() => {
    if (isOnSettingsBranch && !settingsOpen) setSettingsOpen(true);
    // Intentionally only opens (never auto-closes) so the user's manual
    // collapse persists once they navigate away.
  }, [isOnSettingsBranch, settingsOpen]);
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_OPEN_KEY, settingsOpen ? "1" : "0"); } catch (_) { /* localStorage disabled */ }
  }, [settingsOpen]);
  const demoMode = !!state?.tenant?.demo_mode;

  useEffect(() => {
    api.get("/commissions/config").then(r => setWebhookSecret(r.data.webhook_secret)).catch(() => {});
    // Refetch on window focus so the banner updates after toggling in Settings
    const onFocus = () => refresh?.();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Inbound-inbox unread badge — surfaces unmatched replies on the
  // sidebar nav item so these ghost messages can't silently pile up.
  // Reuses the existing /inbound/stats endpoint (ships with the
  // dashboard card). Polls every 60s + refetches on focus.
  const [inboundUnmatched, setInboundUnmatched] = useState(0);
  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const { data } = await api.get("/commissions/inbound/stats");
        if (alive) setInboundUnmatched(Number(data?.unmatched_24h || 0));
      } catch (_) { /* stats endpoint not available — hide badge */ }
    }
    tick();
    const id = setInterval(tick, 60000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_STATE_KEY, next ? "1" : "0"); } catch { /* private mode */ }
      return next;
    });
  }

  async function doLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Sidebar — collapsible to a 64px icon rail. State persists in
          localStorage so admins keep their preferred layout across sessions. */}
      <aside
        data-testid="admin-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className={`${collapsed ? "w-16" : "w-64"} shrink-0 border-r border-slate-800 bg-slate-900 flex flex-col h-screen sticky top-0 self-start transition-[width] duration-200`}
      >
        <div className={`${collapsed ? "p-3" : "p-6"} border-b border-slate-800 flex items-center justify-between gap-2`}>
          <Link to="/admin" data-testid="sidebar-brand" className="block min-w-0 flex-1">
            {collapsed ? (
              <div className="w-8 h-8 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <span className="text-cyan-300 font-bold text-sm">SC</span>
              </div>
            ) : (
              <>
                <img src="/brand/steele-cloud-header.png" alt="Steele Cloud — Partner Portal" className="h-9 w-auto" />
                <div className="mt-2 pt-2 border-t border-slate-800">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-400 font-semibold">Partner Portal · Admin</div>
                  <div className="text-xs text-slate-500 mt-1 truncate">{state?.tenant?.name}</div>
                </div>
              </>
            )}
          </Link>
        </div>
        <nav className={`flex-1 ${collapsed ? "p-2" : "p-3"} space-y-0.5 overflow-y-auto overflow-x-hidden`}>
          {NAV.map(({ to, icon: Icon, label, end, testid, children }) => {
            // Per-item right-aligned badge count. Only Inbound Inbox has
            // one for now, but the pattern leaves room for future ones
            // (e.g. Feedback unread count).
            const badgeCount = testid === "nav-inbound-inbox" ? inboundUnmatched : 0;
            return (
            <div key={to}>
              <NavLink
                to={to} end={end} data-testid={testid}
                title={collapsed ? label : undefined}
                // If this item has children AND we're in expanded mode,
                // clicking the row both navigates AND opens the group.
                // (Clicking again while already active just re-opens —
                // to close, the user hits the chevron.)
                onClick={() => {
                  if (children && !collapsed) setSettingsOpen(true);
                }}
                className={({ isActive }) =>
                  `relative flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-950 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-slate-50"
                  }`
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                {!collapsed && <span className="truncate flex-1">{label}</span>}
                {/* Right-aligned unread badge (expanded sidebar). */}
                {!collapsed && badgeCount > 0 && (
                  <span
                    data-testid={`${testid}-badge`}
                    title={`${badgeCount} unmatched · click to open`}
                    className="shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold leading-none"
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
                {/* Red dot in collapsed rail — same signal, less noise. */}
                {collapsed && badgeCount > 0 && (
                  <span
                    data-testid={`${testid}-dot`}
                    className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-slate-900"
                  />
                )}
                {!collapsed && children && (
                  <button
                    type="button"
                    data-testid={`${testid}-toggle`}
                    onClick={(e) => {
                      // Stop the parent NavLink from firing so a toggle
                      // click never navigates. This is the "collapse"
                      // affordance — pure open/close.
                      e.preventDefault();
                      e.stopPropagation();
                      setSettingsOpen((v) => !v);
                    }}
                    className="shrink-0 -mr-1 p-1 rounded hover:bg-slate-700/60 text-slate-400 hover:text-slate-100"
                    aria-label={settingsOpen ? `Collapse ${label}` : `Expand ${label}`}
                    title={settingsOpen ? "Collapse" : "Expand"}
                  >
                    {settingsOpen
                      ? <ChevronDown className="w-3.5 h-3.5" />
                      : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                )}
              </NavLink>
              {/* Sub-nav — indented, only in expanded sidebar and only
                  when the group is toggled open. In the collapsed rail
                  the children just render as top-level icons so they're
                  still reachable one click deep. */}
              {children && (
                collapsed ? (
                  <>
                    {children.map(({ to: cto, icon: CIcon, label: clabel, testid: ctestid }) => (
                      <NavLink
                        key={cto} to={cto} data-testid={ctestid}
                        title={clabel}
                        className={({ isActive }) =>
                          `flex items-center justify-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-slate-950 text-white"
                              : "text-slate-300 hover:bg-slate-800 hover:text-slate-50"
                          }`
                        }
                      >
                        <CIcon className="w-4 h-4 shrink-0" />
                      </NavLink>
                    ))}
                  </>
                ) : settingsOpen ? (
                  <div className="mt-0.5 mb-1 ml-4 pl-3 border-l border-slate-800 space-y-0.5">
                    {children.map(({ to: cto, icon: CIcon, label: clabel, testid: ctestid, tabKey: ctabKey }) => {
                      // The Settings tabs all share ``/admin/settings`` as
                      // their pathname and only differ by ``?tab=`` — so
                      // NavLink's built-in ``isActive`` alone would light
                      // them all up at once. Custom check: pathname must
                      // match AND (for tab children) the ``?tab=`` value
                      // must equal this child's ``tabKey`` (default
                      // ``business`` when no tab param is present).
                      const activeCls = "bg-slate-950 text-white font-medium";
                      const idleCls = "text-slate-400 hover:bg-slate-800 hover:text-slate-100";
                      return (
                        <NavLink
                          key={cto} to={cto} data-testid={ctestid}
                          className={({ isActive }) => {
                            let on = isActive;
                            if (ctabKey) {
                              const p = new URLSearchParams(location.search);
                              const curTab = p.get("tab") || "business";
                              on = location.pathname === "/admin/settings"
                                    && curTab === ctabKey;
                            }
                            return `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-[13px] transition-colors ${on ? activeCls : idleCls}`;
                          }}
                        >
                          <CIcon className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{clabel}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                ) : null
              )}
            </div>
          );})}
        </nav>
        <div className={`${collapsed ? "p-2" : "p-3"} border-t border-slate-800 space-y-1`}>
          {!collapsed && (
            <div className="p-3 text-xs text-slate-500">
              <div className="font-medium text-slate-300 truncate">{state?.user?.name || state?.user?.email}</div>
              <div className="truncate">{state?.user?.email}</div>
            </div>
          )}
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-50 transition-colors`}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`w-full flex items-center ${collapsed ? "justify-center" : "gap-2"} px-3 py-2 rounded-lg text-xs text-slate-500 hover:bg-slate-800 hover:text-slate-300 transition-colors`}
          >
            {collapsed
              ? <ChevronsRight className="w-4 h-4 shrink-0" />
              : <><ChevronsLeft className="w-4 h-4 shrink-0" /><span>Collapse</span></>}
          </button>
        </div>
        {!collapsed && <VersionFooter />}
      </aside>

      {/* Main */}
      <main className="flex-1" style={{ overflowX: "clip" }}>
        <div className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl px-6 py-3 flex items-center gap-3">
          <HeaderSearch testid="admin-header-search" />
          <div className="ml-auto flex items-center gap-3">
            <PartnerUpdatesBell />
          </div>
        </div>
        {demoMode && (
          <div
            data-testid="demo-mode-banner"
            className="sticky top-0 z-30 flex items-center justify-center gap-2 bg-amber-500/15 border-b border-amber-500/40 text-amber-200 text-xs font-semibold uppercase tracking-widest py-2.5 px-4 backdrop-blur"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Demo mode active — no real emails will be dispatched · PDFs are watermarked
          </div>
        )}
        <div className="max-w-7xl xl:max-w-[110rem] 2xl:max-w-[140rem] mx-auto p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function PartnerUpdatesBellImpl() {
  const [state, setState] = useState({ unread_count: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function load() {
    try {
      const { data } = await api.get("/commissions/partner-updates");
      setState(data);
    } catch (_) { /* silent — the bell is best-effort */ }
  }

  useEffect(() => {
    load();
    // Poll every 15s so admins see partner activity within a quarter-min
    // without a refresh.
    const t = setInterval(load, 15000);
    // Also refresh the instant the tab regains focus — covers the "come
    // back from another tab" case where the interval hasn't fired yet.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!e.target.closest("[data-testid='partner-updates-bell-wrap']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markAllRead() {
    setBusy(true);
    try {
      await api.post("/commissions/partner-updates/mark-read");
      await load();
    } finally { setBusy(false); }
  }

  function summarise(it) {
    if (it.action === "note_added") return it.body || "(empty note)";
    if (it.action === "field_updated") return `updated ${it.field}: “${it.new || ""}”`;
    if (it.action === "stage_changed") return `moved to ${it.stage_to}`;
    if (it.action === "syncro_comment_pulled") {
      const prefix = it.hidden === false ? "Syncro reply" : "Syncro private note";
      const tag = it.ticket_number ? ` · #${it.ticket_number}` : "";
      return `${prefix}${tag}: ${it.body || "(empty)"}`;
    }
    if (it.action === "syncro_note_pushed" || it.action === "syncro_pushed") {
      return `Pushed to Syncro: ${it.body || ""}`.trim();
    }
    return it.action.replace(/_/g, " ");
  }

  const badge = state.unread_count > 0;
  return (
    <div className="relative" data-testid="partner-updates-bell-wrap">
      <button
        data-testid="partner-updates-bell"
        onClick={() => setOpen(o => !o)}
        className="relative inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-900 text-sm text-slate-300 transition-colors"
        title={badge ? `${state.unread_count} unread partner update${state.unread_count === 1 ? "" : "s"}` : "Partner updates"}
      >
        <Bell className={`w-4 h-4 ${badge ? "text-amber-300" : "text-slate-400"}`} />
        <span className="hidden md:inline text-xs">Partner updates</span>
        {badge && (
          <span
            data-testid="partner-updates-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center"
          >
            {state.unread_count > 99 ? "99+" : state.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="partner-updates-dropdown"
          className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/40 z-50"
        >
          <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Partner activity</div>
              <div className="text-[11px] text-slate-500">
                {badge ? `${state.unread_count} unread` : "All caught up"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {badge && (
                <button
                  data-testid="partner-updates-mark-read"
                  onClick={markAllRead}
                  disabled={busy}
                  className="text-xs text-cyan-300 hover:text-cyan-200 disabled:opacity-50"
                >
                  Mark all read
                </button>
              )}
              {(state.items || []).length > 0 && state.items.some(x => x.unread) && (
                <button
                  data-testid="partner-updates-clear-all"
                  onClick={markAllRead}
                  disabled={busy}
                  className="text-xs text-slate-400 hover:text-red-300 disabled:opacity-50"
                  title="Clear the notifications panel — everything is treated as read"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
          {(state.items || []).filter(x => x.unread).length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              {(state.items || []).length === 0 ? "No partner activity yet." : "All caught up — no unread updates."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {(state.items || []).filter(x => x.unread).map((it) => {
                const goto = async () => {
                  // Optimistic per-item clear — mark ONLY this update as
                  // read (v1.10.47). Was previously calling the
                  // mark-all-read endpoint which zeroed the whole badge
                  // even when the admin only meant to look at one item.
                  setState((s) => ({
                    ...s,
                    unread_count: Math.max(0, (s.unread_count || 0) - 1),
                    items: (s.items || []).map(x =>
                      x.id === it.id ? { ...x, unread: false } : x),
                  }));
                  try {
                    await api.post(`/commissions/partner-updates/${encodeURIComponent(it.id)}/mark-read`);
                  } catch (_) { /* best-effort — badge will re-sync on next poll */ }
                  setOpen(false);
                  // Deep-link into the exact customer's drawer via ?open=<client_id>.
                  // MigrationBoard picks the param up on mount and auto-pops the drawer.
                  if (it.partner_code && it.client_id) {
                    navigate(`/admin/onboarding/${it.partner_code}?open=${it.client_id}`);
                  } else if (it.partner_code) {
                    navigate(`/admin/onboarding/${it.partner_code}`);
                  }
                };
                return (
                  <li key={it.id}>
                    <button
                      onClick={goto}
                      data-testid={`partner-update-item-${it.id}`}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-950 transition-colors ${it.unread ? "border-l-2 border-amber-400" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        {it.action === "note_added"
                          ? <MessageSquare className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-400" />
                          : <ActivityIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-400" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-slate-100 font-medium truncate">
                            {it.business_name}
                          </div>
                          <div className="text-xs text-slate-400 line-clamp-2 whitespace-pre-wrap">
                            {summarise(it)}
                          </div>
                          <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-2">
                            <span className="text-emerald-300">{it.actor_email}</span>
                            <span>·</span>
                            <span>{new Date(it.created_at).toLocaleString(undefined, {month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit"})}</span>
                            {it.partner_code && (
                              <>
                                <span>·</span>
                                <span className="uppercase tracking-widest">{it.partner_code}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function WebhookBanner({ config }) {
  const [copied, setCopied] = useState(false);
  if (!config) return null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const url = `${backend}${config.webhook_urls.payment_made}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Webhook URL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copy failed");
    }
  }
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 flex items-center gap-4 justify-between" data-testid="webhook-banner">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Syncro webhook URL</div>
        <div className="font-mono text-xs text-slate-300 truncate mt-1">{url}</div>
      </div>
      <button
        data-testid="copy-webhook"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-950 text-sm font-medium transition-colors"
      >
        {copied ? <CheckCircle2 className="w-4 h-4 text-cyan-500" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
